import React, { createContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface PageLoadingContextType {
  isLoading: boolean;
  loadingPage: string;
  setLoading: (loading: boolean) => void;
}

const PageLoadingContext = createContext<PageLoadingContextType | undefined>(undefined);

interface PageLoadingProviderProps {
  children: ReactNode;
}

export const PageLoadingProvider: React.FC<PageLoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState('');
  const visitedPagesRef = useRef<Set<string>>(new Set());
  const location = useLocation();

  // Initialize visited pages from localStorage on component mount
  useEffect(() => {
    // TEMPORARY: Clear localStorage to test About and Services pages
    localStorage.removeItem('visitedPages');
    
    const savedVisitedPages = localStorage.getItem('visitedPages');
    if (savedVisitedPages) {
      const pagesArray = JSON.parse(savedVisitedPages);
      // Filter out the home page so it can show loader on refresh
      const filteredPages = pagesArray.filter((page: string) => page !== '/');
      visitedPagesRef.current = new Set(filteredPages);
      console.log('PageLoading: Loaded visited pages from localStorage:', filteredPages);
    }
  }, []);

  // Function to convert path to page name
  const getPageName = (path: string): string => {
    switch (path) {
      case '/':
        return 'Welcome';
      case '/about':
        return 'About Us';
      case '/products':
        return 'Products';
      case '/services':
        return 'Services';
      case '/projects':
        return 'Our Projects';
      case '/gallery':
        return 'Gallery';
      case '/contact':
        return 'Contact Us';
      default:
        // Handle dynamic routes
        if (path.startsWith('/products/')) {
          return 'Product Details';
        }
        if (path.startsWith('/services/')) {
          return 'Service Details';
        }
        if (path.startsWith('/projects/')) {
          return 'Project Details';
        }
        return 'Page';
    }
  };

  useEffect(() => {
    const currentPath = location.pathname;
    
    // Check if this page has been visited before
    const hasVisitedBefore = visitedPagesRef.current.has(currentPath);
    
    // Check if this is a sub-page (services, products, projects detail pages)
    const isSubPage = currentPath.startsWith('/services/') || 
                     currentPath.startsWith('/products/') || 
                     currentPath.startsWith('/projects/');
    
    console.log('PageLoading: Current path:', currentPath, 'Visited before:', hasVisitedBefore, 'Is sub-page:', isSubPage);
    
    // Don't show loader for sub-pages (card clicks)
    if (isSubPage) {
      console.log('PageLoading: Skipping loader for sub-page:', currentPath);
      setIsLoading(false);
      setLoadingPage('');
      return;
    }
    
    // Show loader if this page hasn't been visited before AND it's not the home page
    if (!hasVisitedBefore && currentPath !== '/') {
      console.log('PageLoading: Showing loader for:', currentPath);
      setLoadingPage(getPageName(currentPath));
      setIsLoading(true);
      
      // Add current page to visited pages
      visitedPagesRef.current.add(currentPath);
      
      // Save to localStorage
      localStorage.setItem('visitedPages', JSON.stringify([...visitedPagesRef.current]));
      
      // Hide loader after 1 second for non-home pages
      const timer = setTimeout(() => {
        console.log('PageLoading: Hiding loader after timeout');
        setIsLoading(false);
        setLoadingPage('');
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (currentPath === '/') {
      // Always show loader for home page on refresh
      console.log('PageLoading: Showing loader for home page on refresh');
      setLoadingPage(getPageName(currentPath));
      setIsLoading(true);
      
      // Hide loader after 3 seconds for home page
      const timer = setTimeout(() => {
        console.log('PageLoading: Hiding loader after timeout');
        setIsLoading(false);
        setLoadingPage('');
      }, 3000);
      
      return () => clearTimeout(timer);
    } else {
      // Ensure loader is hidden if page has been visited or is home page
      console.log('PageLoading: Page already visited or is home, hiding loader');
      setIsLoading(false);
      setLoadingPage('');
    }
  }, [location.pathname]);

  // Cleanup effect to ensure loader is hidden when component unmounts
  useEffect(() => {
    return () => {
      setIsLoading(false);
      setLoadingPage('');
    };
  }, []);

  const setLoading = (loading: boolean) => {
    if (loading) {
      const currentPath = location.pathname;
      
      // Don't show loader for parent/dropdown items (Services, Products, Projects)
      const isParentItem = currentPath === '/services' || currentPath === '/products' || currentPath === '/projects';
      
      if (isParentItem) {
        // Don't show loader for parent items, just close any dropdowns
        return;
      }
      
      // Show loader for main navigation items only
      setLoadingPage(getPageName(currentPath));
      setIsLoading(true);
      
      // Add current page to visited pages
      visitedPagesRef.current.add(currentPath);
      
      // Save to localStorage
      localStorage.setItem('visitedPages', JSON.stringify([...visitedPagesRef.current]));
      
      // Hide loader after appropriate time based on page type
      if (currentPath === '/') {
        // Hide loader after 3 seconds for home page
        setTimeout(() => {
          setIsLoading(false);
          setLoadingPage('');
        }, 3000);
      } else {
        // Hide loader after 1 second for other pages
        setTimeout(() => {
          setIsLoading(false);
          setLoadingPage('');
        }, 1000);
      }
    } else {
      setIsLoading(false);
      setLoadingPage('');
    }
  };

  return (
    <PageLoadingContext.Provider value={{ isLoading, loadingPage, setLoading }}>
      {children}
    </PageLoadingContext.Provider>
  );
};

// Export the context for use in the hook file
export { PageLoadingContext }; 